import {
  CellContext,
  columnVisibilityFeature,
  createColumnHelper,
  tableFeatures
} from "@tanstack/react-table"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { exists } from "shared/lib/exists"
import { getUserColor } from "shared/lib/user/getUserColor"
import { IconSize } from "shared/types/Icon"
import { Button } from "shared/ui/buttons/Button"
import { Chip } from "shared/ui/chip/Chip"
import { DateTime } from "shared/ui/date-time/DateTime"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { TextAvatar } from "shared/ui/text-avatar/TextAvatar"

import { ProjectHistoryEntry } from "entities/projects/types/ProjectHistoryEntry"
import { formatRoomTitle } from "entities/rooms/ui"
import { ParseKeys, TFunction } from "i18next"

import { ProjectHistoryDisplayRow } from "../types/ProjectHistoryTable"

// react-table v9 requires every table to register the features it uses. The
// history table only needs column visibility, for `row.getVisibleCells()`.
export const projectHistoryTableFeatures = tableFeatures({
  columnVisibilityFeature
})

const fieldUnitMap = {
  inflationSurcharge: "%",
  area: "m2",
  height: "m",
  airVolumePerArea: "l/s",
  airVolumePerPerson: "l/s",
  soundPressureLevel: "dB(A)",
  sup: "m3/h",
  oda: "m3/h",
  eta: "m3/h",
  eha: "m3/h",
  tra: "m3/h",
  rca: "m3/h",
  ida: "m3/h",
  sec: "m3/h"
}
const booleanFields = ["isAlternative", "isSpecialPriceGroup"]
const colorFields = ["ui"]

function getInitials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/)
  if (parts.length === 0) return ""
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
}

function renderFormattedValue(
  value: string,
  field: string,
  entityType: string,
  t: TFunction
): string {
  if (colorFields.includes(field)) {
    if (value === "-") return "-"
    const data = JSON.parse(value)
    return t(`Color.${data.color}` as ParseKeys, {
      returnObjects: false
    }).toString()
  }

  if (entityType === "Room" && field === "utilization") {
    return t(`RoomType.${value}` as ParseKeys, {
      returnObjects: false
    }).toString()
  }

  const unit = fieldUnitMap[field as keyof typeof fieldUnitMap]
  if (exists(unit)) {
    return `${value} ${unit}`
  }

  if (booleanFields.includes(field)) {
    return t(`Boolean.${value}` as ParseKeys, {
      returnObjects: false
    }).toString()
  }

  return value
}

function OpenElementButton({
  entry,
  isDeleted,
  getElementUrl
}: {
  entry: ProjectHistoryEntry
  isDeleted: boolean
  getElementUrl: (entry: ProjectHistoryEntry) => string
}) {
  const { t } = useTranslation()

  if (isDeleted) {
    return (
      <span className="project-history-table__open-btn project-history-table__open-btn--disabled">
        <Icon name={IconName.OPEN} />
        <span>{t("Project.History.NotAvailable")}</span>
      </span>
    )
  }

  return (
    <a
      href={getElementUrl(entry)}
      target="_blank"
      rel="noreferrer"
      className="btn btn--link btn--borderless project-history-table__open-btn"
    >
      <Icon name={IconName.OPEN} />
      <span>{t("Project.History.OpenEntity")}</span>
    </a>
  )
}

function renderTimestampCell(
  info: CellContext<
    typeof projectHistoryTableFeatures,
    ProjectHistoryDisplayRow,
    unknown
  >
) {
  const row = info.row.original
  const entry = row.kind === "changeDetail" ? row.parentEntry : row.entry

  return (
    <div className="project-history-table__timestamp">
      <DateTime isoTimestamp={entry.timestamp}>
        <DateTime.Date />
        <DateTime.Time className="project-history-table__timestamp-time" />
      </DateTime>
    </div>
  )
}

function renderUserCell(
  info: CellContext<
    typeof projectHistoryTableFeatures,
    ProjectHistoryDisplayRow,
    unknown
  >,
  t: TFunction
) {
  const row = info.row.original
  const { user } = row.kind === "changeDetail" ? row.parentEntry : row.entry

  return (
    <div className="project-history-table__user">
      <TextAvatar
        initials={getInitials(user?.name || "") || "?"}
        color={getUserColor(user?.id)}
        className="text-avatar--xs"
      />
      <span>{user?.name || t("Project.History.UnknownUser")}</span>
    </div>
  )
}

function renderEntityTypeCell(
  info: CellContext<
    typeof projectHistoryTableFeatures,
    ProjectHistoryDisplayRow,
    unknown
  >,
  t: TFunction
) {
  const row = info.row.original
  const entry = row.kind === "changeDetail" ? row.parentEntry : row.entry

  return t(`Project.History.EntityType.${entry.entityType}` as ParseKeys)
}

function formatEntityLabel(
  entity: ProjectHistoryEntry["entity"],
  fallback?: string | null | undefined
) {
  if (!entity) {
    return fallback ?? "-"
  }

  if (entity.type === "Project") {
    return entity.title ?? "-"
  }

  if (entity.type === "Floor") {
    return entity.label ?? "-"
  }

  if (entity?.type === "Room") {
    return formatRoomTitle(entity.number, entity.label)
  }

  if (entity.type === "ProductPosition") {
    return entity.displayPosition ?? entity.variantKey ?? "-"
  }

  return fallback ?? "-"
}

function renderEntityIdCell(
  info: CellContext<
    typeof projectHistoryTableFeatures,
    ProjectHistoryDisplayRow,
    unknown
  >
) {
  const row = info.row.original

  const { label, entity } =
    row.kind === "changeDetail" ? row.parentEntry : row.entry

  return formatEntityLabel(entity, label)
}

function renderFieldCell(
  info: CellContext<
    typeof projectHistoryTableFeatures,
    ProjectHistoryDisplayRow,
    unknown
  >,
  t: TFunction
) {
  const row = info.row.original

  if (row.kind === "changeDetail") {
    return t(`${row.parentEntry.entityType}.${row.field}` as ParseKeys)
  }

  const { changes, entityType } = row.entry

  if (!changes || changes?.length === 0) return "-"

  if (changes?.length === 1) {
    return t(`${entityType}.${changes[0].field}` as ParseKeys)
  }

  return (
    <Button
      className="project-history-table__expand-btn"
      onClick={row.onToggle}
      aria-expanded={row.isExpanded}
      icon={
        row.isExpanded ? IconName.SMALL_CARET_DOWN : IconName.SMALL_CARET_RIGHT
      }
      iconSize={IconSize.XS}
      label={t("Project.History.MultipleFields")}
    />
  )
}

function renderValueCell(
  info: CellContext<
    typeof projectHistoryTableFeatures,
    ProjectHistoryDisplayRow,
    unknown
  >,
  kind: "old" | "new",
  t: TFunction
) {
  const row = info.row.original

  if (row.kind === "changeDetail") {
    const raw = kind === "old" ? row.oldValue : row.newValue
    const value = raw != null ? String(raw) : "-"
    return renderFormattedValue(value, row.field, row.parentEntry.entityType, t)
  }

  const { changes, fromEntity, toEntity, action, entityType } = row.entry

  if (action === "UPDATE") {
    if (changes?.length !== 1) return "-"

    const { field, oldValue, newValue } = changes[0]
    const raw = kind === "old" ? oldValue : newValue
    const value = raw != null ? String(raw) : "-"
    return renderFormattedValue(value, field, entityType, t)
  }
  if (action === "MOVE_IN" && kind === "old") {
    return formatEntityLabel(fromEntity, t("Project.History.NotAvailable"))
  }
  if (action === "MOVE_OUT" && kind === "new") {
    return formatEntityLabel(toEntity, t("Project.History.NotAvailable"))
  }
  return "-"
}

function renderActionCell(
  info: CellContext<
    typeof projectHistoryTableFeatures,
    ProjectHistoryDisplayRow,
    unknown
  >,
  t: TFunction
) {
  const row = info.row.original
  const { action } = row.kind === "changeDetail" ? row.parentEntry : row.entry
  return (
    <Chip
      className={classNames(
        `chip--small project-history-chip--action-${action.toLowerCase()}`
      )}
    >
      {t(`Project.History.Action.${action}` as ParseKeys) as string}
    </Chip>
  )
}

function renderOpenCell(
  info: CellContext<
    typeof projectHistoryTableFeatures,
    ProjectHistoryDisplayRow,
    unknown
  >,
  getElementUrl: (entry: ProjectHistoryEntry) => string
) {
  const row = info.row.original
  const entry = row.kind === "changeDetail" ? row.parentEntry : row.entry
  return (
    <OpenElementButton
      entry={entry}
      isDeleted={entry.action === "DELETE"}
      getElementUrl={getElementUrl}
    />
  )
}

const columnHelper = createColumnHelper<
  typeof projectHistoryTableFeatures,
  ProjectHistoryDisplayRow
>()

export function makeColumns(
  t: TFunction,
  getElementUrl: (entry: ProjectHistoryEntry) => string
) {
  return [
    columnHelper.display({
      id: "timestamp",
      header: t("Project.History.Column.Timestamp"),
      cell: info => renderTimestampCell(info)
    }),
    columnHelper.display({
      id: "user",
      header: t("Project.History.Column.Editor"),
      cell: info => renderUserCell(info, t)
    }),
    columnHelper.display({
      id: "entityType",
      header: t("Project.History.Column.EntityType"),
      cell: info => renderEntityTypeCell(info, t)
    }),
    columnHelper.display({
      id: "entityId",
      header: t("Project.History.Column.EntityId"),
      cell: info => renderEntityIdCell(info)
    }),
    columnHelper.display({
      id: "field",
      header: t("Project.History.Column.Field"),
      cell: info => renderFieldCell(info, t)
    }),
    columnHelper.display({
      id: "oldValue",
      header: t("Project.History.Column.OldValue"),
      cell: info => renderValueCell(info, "old", t)
    }),
    columnHelper.display({
      id: "newValue",
      header: t("Project.History.Column.NewValue"),
      cell: info => renderValueCell(info, "new", t)
    }),
    columnHelper.display({
      id: "action",
      header: t("Project.History.Column.Action"),
      cell: info => renderActionCell(info, t)
    }),
    columnHelper.display({
      id: "open",
      header: t("Project.History.Column.Link"),
      cell: info => renderOpenCell(info, getElementUrl)
    })
  ]
}
